import React from "react";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Divider as MuiDivider,
  Link,
  Paper,
  Typography as MuiTypography,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import MaterialTable from "material-table";
import CopyIcon from "@material-ui/icons/FileCopy";
import { ExpandMore } from "@material-ui/icons";

import { NavLink } from "react-router-dom";
import { useApp } from "../../../../AppProvider";
import { copyToClipboard, dateFormatter } from "../../../../utils";
import WaterbodyInfo from "./WaterbodyInfo";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { WATERBODIES_TABLE_COLUMNS } from "./WaterbodiesConstants";
import { useWaterbodies } from "../hooks/useWaterbodies";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Typography = styled(MuiTypography)(spacing);

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)}px);
  height: 100%;
  width: 100%;
`;
function WaterbodiesSetup() {
  const {
    selectedWaterbody,
    setSelectedWaterbody,
    data,
    isFetching,
    error,
    refetch,
  } = useWaterbodies();
  const { doToast } = useApp();
  const { getAccessTokenSilently } = useAuth0();

  const handleDelete = (oldData) => {
    return (async () => {
      try {
        if (oldData) {
          const token = await getAccessTokenSilently();
          const headers = { Authorization: `Bearer ${token}` };
          await axios.delete(
            `${process.env.REACT_APP_ENDPOINT}/api/data-list-waterbodies/${oldData.waterbody_ndx}`,
            { headers }
          );
          doToast("success", "This entry was deleted from the database");
          if (selectedWaterbody === oldData.waterbody_ndx) {
            setSelectedWaterbody(null);
          }

          await refetch();
        } else {
          doToast("error", "Something went wrong");
        }
      } catch (err) {
        console.error(err);
        const message = err?.message ?? "Something went wrong";
        doToast("error", message);
      }
    })();
  };

  if (error) return "An error has occurred: " + error.message;
  return (
    <React.Fragment>
      <Helmet title="Waterbodies Setup" />
      <Typography variant="h3" gutterBottom display="inline">
        Waterbodies Setup
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/dashboard">
          Dashboard
        </Link>
        <Typography>Waterbodies Setup</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Box>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box style={{ width: "100%" }}>
              <Typography variant="h3">Waterbodies Selection</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableWrapper>
              <MaterialTable
                id="Waterbodies Setup"
                title={`Waterbodies Setup ${dateFormatter(
                  new Date(),
                  "MM/DD/YYYY, h:mm A"
                )}`}
                loading={isFetching || !data}
                columns={WATERBODIES_TABLE_COLUMNS}
                data={data}
                localization={{
                  toolbar: { searchPlaceholder: "Waterbodies Selection" },
                }}
                components={{
                  Container: (props) => <div {...props} />,
                }}
                editable={{
                  onRowDelete: handleDelete,
                }}
                actions={[
                  {
                    icon: CopyIcon,
                    tooltip: "Copy Data",
                    isFreeAction: true,
                    onClick: () => {
                      try {
                        copyToClipboard(data, WATERBODIES_TABLE_COLUMNS, () =>
                          doToast(
                            "success",
                            "Data was copied to your clipboard."
                          )
                        );
                      } catch (error) {
                        const message =
                          error?.message ?? "Something went wrong";
                        doToast("error", message);
                      }
                    },
                  },
                  () => ({
                    icon: "edit",
                    tooltip: "Edit",
                    onClick: (event, rowData) => {
                      setSelectedWaterbody(rowData?.waterbody_ndx);
                    },
                  }),
                  {
                    icon: "add_box",
                    tooltip: "Add",
                    isFreeAction: true,
                    onClick: () => {
                      setSelectedWaterbody(0);
                    },
                  },
                ]}
                options={{
                  filtering: true,
                  emptyRowsWhenPaging: false,
                  showTitle: false,
                  columnsButton: true,
                  exportButton: true,
                  exportAllData: true,
                  addRowPosition: "first",
                  pageSize: 10,
                  pageSizeOptions: [10, 30, 50, 100, 200],
                  padding: "dense",
                  searchFieldAlignment: "left",
                  maxBodyHeight:
                    "calc(100vh - 64px - 48px - 106px - 48px - 64px - 204px - 100px)",
                }}
              />
            </TableWrapper>
          </AccordionDetails>
        </Accordion>

        <Box
          style={{
            display:
              selectedWaterbody || selectedWaterbody === 0 ? "block" : "none",
          }}
        >
          <Paper style={{ margin: "16px 0" }}>
            <Box
              p={4}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h4">
                {selectedWaterbody === 0
                  ? "Creating New Waterbody"
                  : "Editing Existing Waterbody"}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setSelectedWaterbody(null)}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
          <WaterbodyInfo
            selectedWaterbody={selectedWaterbody}
            setSelectedWaterbody={setSelectedWaterbody}
            refetchWaterbodies={refetch}
          />
          <Paper style={{ margin: "16px 0" }}>
            <Box
              p={4}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h4">
                {selectedWaterbody === 0
                  ? "Creating New Waterbody"
                  : "Editing Existing Waterbody"}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setSelectedWaterbody(null)}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </React.Fragment>
  );
}

export default WaterbodiesSetup;
